"use client"

import { useMemo } from "react"
import type { TFunction } from "i18next"

type TransferRow = {
  transferId: number
  question?: string
  date?: string | Date
  is_urgent?: boolean
  is_read?: boolean
  is_open?: boolean
  is_important?: boolean
  is_ignored?: boolean
  collaborations?: any[]
  assigned_by?: string
}

interface UseNotificationDataProps {
  notifications: {
    messages: {
      [userId: string]: {
        has: boolean
        firstAt: string | null
        unseenTimes: string[]
      }
    }
    transfers: Record<string, TransferRow[]> | any
    lastUpdated: number
  }
  myAssignedIds: number[]
  myUsers: any[]
  t: TFunction
}

export const useNotificationData = ({
  notifications,
  myAssignedIds,
  myUsers,
  t,
}: UseNotificationDataProps) => {

  const countAfter = (userId: string | number, since?: string | Date | null) => {
    const entry = notifications.messages?.[userId as any]
    if (!entry || !entry.unseenTimes?.length) return 0
    if (!since) return entry.unseenTimes.length
    const ts = typeof since === "string" ? new Date(since).getTime() : since.getTime()
    return entry.unseenTimes.filter(d => new Date(d).getTime() > ts).length
  }

  const latestTransferByUser = useMemo(() => {
    const map: Record<string, any> = {}

    const entries = Object.entries(notifications.transfers ?? {}) as Array<[string, TransferRow[]]>

    entries.forEach(([userId, transfers]) => {
      (transfers || [])
        .filter(tr =>
          myAssignedIds.includes(tr.transferId) &&
          !tr.is_read &&
          !tr.is_ignored
        )
        .forEach(tr => {
          const dateMs = tr.date ? new Date(tr.date as any).getTime() : Date.now()
          const prev = map[userId]
          if (!prev || dateMs > prev.__dateMs) {
            map[userId] = {
              userId,
              transferId: tr.transferId,
              type: "transfer",
              content: tr.question ?? "", 
              date: tr.date || new Date().toISOString(),
              urgent: !!tr.is_urgent,
              read: !!tr.is_read,
              open: !!tr.is_open,
              important: !!tr.is_important,
              ignored: !!tr.is_ignored,
              collaborations: tr.collaborations,
              assigned_by: tr.assigned_by,
              assigned_to: (tr as any).assigned_to ?? null, 
              __dateMs: dateMs,
            }
          }
        })
    })

    Object.values(map).forEach((item: any) => {
      item.newCount = countAfter(item.userId, item.date || null)
      delete item.__dateMs
    })

    return map
  }, [notifications.transfers, myAssignedIds, notifications.lastUpdated])

  const uniqueTransfers = useMemo(
    () => Object.values(latestTransferByUser),
    [latestTransferByUser]
  )

  const urgentTransfers = useMemo(
    () => uniqueTransfers.filter((t: any) => t.urgent),
    [uniqueTransfers]
  )

  const nonUrgentTransfers = useMemo(
    () => uniqueTransfers.filter((t: any) => !t.urgent),
    [uniqueTransfers]
  )

  const regularNotifications = useMemo(() => {
    const transferUserIds = new Set(
      uniqueTransfers.map((t: any) => String(t.userId))
    )

    const messages = Object.entries(notifications.messages || {})
      .filter(([userId, entry]: any) => {
        const numId = Number(userId)
        return (
          entry?.has &&
          !transferUserIds.has(String(userId)) &&
          myAssignedIds.includes(numId)
        )
      })
      .map(([userId, entry]: any) => {
        const user = myUsers.find(u => String(u.id) === String(userId))
        const date = user?.lastMessageDate || entry.firstAt || new Date().toISOString()
        const newCount = (entry?.unseenTimes || []).length
        return {
          userId,
          type: "message",
          content: t("new_message_received"),
          date,
          read: !entry?.has,
          newCount,
        }
      })

    return [...messages, ...nonUrgentTransfers].sort(
      (a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()
    )
  }, [
    notifications.messages,
    notifications.lastUpdated,
    myAssignedIds,
    myUsers,
    t,
    uniqueTransfers,
    nonUrgentTransfers,
  ])

  return {
    allTransfers: uniqueTransfers, 
    uniqueTransfers,
    urgentTransfers,
    nonUrgentTransfers,
    regularNotifications,
  }
}
